
import { Component, Vue, Prop } from 'vue-property-decorator';
import { StoreModel } from '@/models/store';
import { PurchaseOrders } from '@/collections/purchase_orders';
import { PurchaseOrderModel } from '@/models/purchase_order';
import { SupplierList } from '@/collections/suppliers';
import { SupplierModel } from '@/models/supplier';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
import dateFormat from 'dateformat';
import { EventBus } from '@/main';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { ax, getIdentityToken } from '@/services/http';

@Component
export default class PurchaseOrderList extends Vue {
  @Prop({ default: () => new StoreModel() }) store!: StoreModel;

  private purchaseOrders: any[] = [];
  private totalPurchaseOrders = 0;
  private loading = false;
  private search = '';
  private filters = {
    status: '',
    supplierId: null as number | null,
    locationId: null as number | null,
    source: '',
    orderDateFrom: '',
    orderDateTo: '',
    departureDateFrom: '',
    departureDateTo: '',
    expectedDeliveryDateFrom: '',
    expectedDeliveryDateTo: ''
  };
  private options = {
    page: 1,
    itemsPerPage: 10,
    sortBy: ['orderDate'],
    sortDesc: [true]
  };
  private headers = [
    { text: 'PO Number', value: 'poNumber', width: '140px' },
    { text: 'Supplier', value: 'supplierName', width: '180px' },
    { text: 'Location', value: 'locationName', width: '160px' },
    { text: 'Source', value: 'source', width: '140px' },
    { text: 'Status', value: 'status', width: '160px' },
    { text: 'Order Date', value: 'orderDate', width: '140px' },
    { text: 'Departure', value: 'departureDate', width: '140px' },
    { text: 'Expected Delivery', value: 'expectedDeliveryDate', width: '160px' },
    { text: 'Total', value: 'totalAmount', width: '120px' },
    { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '100px' }
  ];
  private purchaseOrdersCollection = new PurchaseOrders();
  private suppliers: SupplierModel[] = [];
  private locations: StoreLocationModel[] = [];
  private loadingSuppliers = false;
  private loadingLocations = false;

  // Dialog states
  private createPODialogVisible = false;
  private selectedSupplierId: number | null = null;

  // Import PO dialog
  private showCreateForImportDialog = false;
  private creatingForImport = false;
  private importFormValid = false;
  private importDateMenu = false;
  private importPO = {
    supplierId: null as number | null,
    locationId: null as number | null,
    expectedDeliveryDate: '',
    notes: ''
  };

  // Export dialog
  private showExportDialog = false;
  private exportFormat: 'csv' | 'xlsx' | 'pdf' = 'csv';
  private exporting = false;

  // Filter panel
  private filterPanelExpanded: number | undefined = 0;

  // Date filter menus
  private orderDateFromMenu = false;
  private orderDateToMenu = false;
  private departureDateFromMenu = false;
  private departureDateToMenu = false;
  private expectedDeliveryDateFromMenu = false;
  private expectedDeliveryDateToMenu = false;

  // Debounced search handler
  private debouncedSearch: any = null;

  // Validation rule
  private locationRequiredRule = (v: any) => !!v || 'Store location is required';

  async created() {
    // Create debounced search function (300ms delay)
    this.debouncedSearch = this.debounce(this.applyFilterAndResetPage, 300);

    // Initialize filters from URL parameters
    this.initializeFromURLParams();

    await this.fetchOrdersData();
    await this.fetchSuppliers();
    await this.fetchLocations();
  }

  beforeDestroy() {
    // Clean up debounce timer
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel();
    }
  }

  private initializeFromURLParams() {
    const { status, supplierId, locationId } = this.$route.query;

    if (status && typeof status === 'string') {
      this.filters.status = status;
    }

    if (supplierId && typeof supplierId === 'string') {
      this.filters.supplierId = parseInt(supplierId);
    }

    if (locationId && typeof locationId === 'string') {
      this.filters.locationId = parseInt(locationId);
    }
  }

  private async fetchOrdersData() {
    await this.fetchOrders();
  }

  private async fetchOrders() {
    this.loading = true;
    try {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const orderBy = sortBy.length > 0 ? sortBy[0] : 'createdAt';
      const orderDir = sortDesc.length > 0 && sortDesc[0] ? 'desc' : 'asc';

      // Determine the actual status filter to send to the backend
      let statusFilter = this.filters.status;
      if (statusFilter) {
        switch (statusFilter) {
          case 'open':
            statusFilter = 'draft,confirmed,partial_received';
            break;
          case 'expecting':
            statusFilter = 'confirmed,partial_received';
            break;
        }
      }

      await this.purchaseOrdersCollection.fetch({
        page,
        limit: itemsPerPage,
        search: this.search,
        status: statusFilter,
        source: this.filters.source || undefined,
        supplierId: this.filters.supplierId || undefined,
        locationId: this.filters.locationId || undefined,
        fromDate: this.filters.orderDateFrom || undefined,
        toDate: this.filters.orderDateTo || undefined,
        departureDateFrom: this.filters.departureDateFrom || undefined,
        departureDateTo: this.filters.departureDateTo || undefined,
        expectedDeliveryDateFrom: this.filters.expectedDeliveryDateFrom || undefined,
        expectedDeliveryDateTo: this.filters.expectedDeliveryDateTo || undefined,
        sortBy: orderBy,
        sortDir: orderDir
      });

      this.purchaseOrders = this.purchaseOrdersCollection.items;
      this.totalPurchaseOrders = this.purchaseOrdersCollection.total;
    } catch (error) {
      this.$emit('error', 'Failed to load purchase orders');
      console.error('Error fetching purchase orders:', error);
    } finally {
      this.loading = false;
    }
  }

  private async fetchSuppliers() {
    this.loadingSuppliers = true;
    try {
      const suppliersCollection = new SupplierList();
      await suppliersCollection.fetch();
      this.suppliers = suppliersCollection.items;
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      this.loadingSuppliers = false;
    }
  }

  private async fetchLocations() {
    this.loadingLocations = true;
    try {
      const locationsList = new StoreLocationList();
      await locationsList.fetch();
      this.locations = locationsList.items;
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      this.loadingLocations = false;
    }
  }

  get statusOptions() {
    const baseOptions = this.purchaseOrdersCollection.getStatusOptions();
    return [
      { text: 'Open Orders', value: 'open' },
      { text: 'Expected Deliveries', value: 'expecting' },
      ...baseOptions
    ];
  }

  get supplierOptions() {
    return this.suppliers;
  }

  get locationOptions() {
    return this.locations;
  }

  get sourceOptions() {
    return [
      { text: 'Manual', value: 'manual' },
      { text: 'Automated', value: 'automated' }
    ];
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.search ||
      this.filters.status ||
      this.filters.source ||
      this.filters.supplierId ||
      this.filters.locationId ||
      this.filters.orderDateFrom ||
      this.filters.orderDateTo ||
      this.filters.departureDateFrom ||
      this.filters.departureDateTo ||
      this.filters.expectedDeliveryDateFrom ||
      this.filters.expectedDeliveryDateTo
    );
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.search) count++;
    if (this.filters.status) count++;
    if (this.filters.source) count++;
    if (this.filters.supplierId) count++;
    if (this.filters.locationId) count++;
    if (this.filters.orderDateFrom || this.filters.orderDateTo) count++;
    if (this.filters.departureDateFrom || this.filters.departureDateTo) count++;
    if (this.filters.expectedDeliveryDateFrom || this.filters.expectedDeliveryDateTo) count++;
    return count;
  }

  getStatusLabel(status: string): string {
    const option = this.statusOptions.find(opt => opt.value === status);
    return option ? option.text : status;
  }

  getLocationName(locationId: number | string | null): string {
    if (!locationId) return '-';
    // Convert both to strings for comparison since API returns string IDs
    const location = this.locations.find(loc => String(loc.id) === String(locationId));
    return location ? location.name : '-';
  }

  applyFilterAndResetPage() {
    this.options.page = 1;
    this.fetchOrders();
  }

  clearAllFilters() {
    this.search = '';
    this.filters.status = '';
    this.filters.source = '';
    this.filters.supplierId = null;
    this.filters.locationId = null;
    this.filters.orderDateFrom = '';
    this.filters.orderDateTo = '';
    this.filters.departureDateFrom = '';
    this.filters.departureDateTo = '';
    this.filters.expectedDeliveryDateFrom = '';
    this.filters.expectedDeliveryDateTo = '';
    this.applyFilterAndResetPage();
  }

  openCreatePODialog() {
    this.selectedSupplierId = null;
    this.createPODialogVisible = true;
    if (!this.suppliers || this.suppliers.length === 0) {
      this.fetchSuppliers();
    }
  }

  closeCreatePODialog() {
    this.createPODialogVisible = false;
    this.selectedSupplierId = null;
  }

  closeCreateForImportDialog() {
    this.showCreateForImportDialog = false;
    this.importPO = {
      supplierId: null,
      locationId: null,
      expectedDeliveryDate: '',
      notes: ''
    };
    (this.$refs.importForm as Vue & { reset: () => void }).reset();
  }

  async createPOForImport() {
    if (!(this.$refs.importForm as Vue & { validate: () => boolean }).validate()) {
      return;
    }

    this.creatingForImport = true;
    try {
      const response = await PurchaseOrderModel.createForImport({
        supplierId: this.importPO.supplierId || undefined,
        locationId: this.importPO.locationId || undefined,
        expectedDeliveryDate: this.importPO.expectedDeliveryDate || undefined,
        notes: this.importPO.notes || undefined
      });
      this.$router.push(`/purchase-orders/${response.data.id}?import=true`);
      EventBus.$emit('show-snackbar', {
        message: 'Purchase order created successfully',
        color: 'success'
      });

      this.closeCreateForImportDialog();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', {
        message: `Failed to create purchase order: ${error.message}`,
        color: 'error'
      });
    } finally {
      this.creatingForImport = false;
    }
  }

  async exportPurchaseOrders() {
    this.exporting = true;

    try {
      const params = this.buildExportParams();
      const token = getIdentityToken();

      const response = await ax.get('/purchase_orders/export', {
        params,
        responseType: 'blob',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      const blobUrl = window.URL.createObjectURL(response.data);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `purchase_orders_${new Date().toISOString().split('T')[0]}.${this.exportFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

      this.showExportDialog = false;

      EventBus.$emit('show-snackbar', {
        message: 'Export completed successfully.',
        color: 'success'
      });
    } catch (error: any) {
      console.error('Export error:', error);
      EventBus.$emit('show-snackbar', {
        message: error.response?.data?.message || 'Export failed. Please try again.',
        color: 'error'
      });
    } finally {
      this.exporting = false;
    }
  }

  buildExportParams() {
    const { sortBy, sortDesc } = this.options;

    let statusFilter = this.filters.status;
    if (statusFilter) {
      switch (statusFilter) {
        case 'open':
          statusFilter = 'draft,confirmed,partial_received';
          break;
        case 'expecting':
          statusFilter = 'confirmed,partial_received';
          break;
      }
    }

    return {
      format: this.exportFormat,
      status: statusFilter || undefined,
      supplierId: this.filters.supplierId || undefined,
      locationId: this.filters.locationId || undefined,
      source: this.filters.source || undefined,
      search: this.search || undefined,
      sortBy: sortBy.length > 0 ? sortBy[0] : 'createdAt',
      sortDir: sortDesc.length > 0 && sortDesc[0] ? 'DESC' : 'ASC'
    };
  }

  navigateToCreatePO() {
    if (this.selectedSupplierId) {
      this.$router.push(`/suppliers/${this.selectedSupplierId}/purchase-orders/create`);
      this.closeCreatePODialog();
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'draft':
        return '#9E9E9E';
      case 'confirmed':
        return '#9C27B0';
      case 'partial_received':
        return '#FF9800';
      case 'received':
        return '#4CAF50';
      case 'completed':
        return '#009688';  // Teal
      case 'canceled':
        return '#F44336';
      default:
        return '#9E9E9E';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'draft':
        return 'mdi-pencil';
      case 'confirmed':
        return 'mdi-check-circle';
      case 'partial_received':
        return 'mdi-package-variant';
      case 'received':
        return 'mdi-package-variant-closed';
      case 'completed':
        return 'mdi-check-circle-outline';
      case 'canceled':
        return 'mdi-close-circle';
      default:
        return 'mdi-information';
    }
  }

  formatStatus(status: string): string {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'confirmed':
        return 'Confirmed';
      case 'partial_received':
        return 'Partially Received';
      case 'received':
        return 'Received';
      case 'completed':
        return 'Completed';
      case 'canceled':
        return 'Canceled';
      default:
        return status;
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return dateFormat(new Date(dateString), 'mmm d, yyyy');
  }

  formatCurrency(value: number): string {
    return CurrencyHelper.formatCurrency(value);
  }

  get canManagePurchaseOrders(): boolean {
    const validateValues = [
      {
        code: 'operational_mode',
        value: 'unlocked'
      }
    ];
    const validateResult = this.store.validateRule(validateValues);
    return validateResult.isValidate;
  }

  /**
   * Debounce utility function
   * Creates a debounced version of a function that delays execution
   * until after the specified wait time has elapsed since the last call
   */
  private debounce(func: Function, wait: number) {
    let timeout: number | null = null;

    const debounced = (...args: any[]) => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
      timeout = window.setTimeout(() => {
        func.apply(this, args);
      }, wait);
    };

    // Add cancel method to clear pending execution
    debounced.cancel = () => {
      if (timeout !== null) {
        clearTimeout(timeout);
        timeout = null;
      }
    };

    return debounced;
  }
}
